import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useApi } from '../../../../hooks';
import { safeUrl, sanitizeHtml } from './sanitizeHtml';
import type { StateDocument, StateNote, TaxData } from './types';

type Load =
  | { quoteId: string; status: 'loading' }
  | { quoteId: string; status: 'loaded'; data: TaxData | null }
  | { quoteId: string; status: 'failed' };

// Formatting and lists from InsCipher's HTML, which Tailwind's reset would otherwise flatten.
const RICH_TEXT = '[&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p:not(:last-child)]:mb-2 [&_ul]:list-disc [&_ul]:pl-5';

const hasContent = (data: TaxData | null): data is TaxData =>
  !!data && !!(data.stampingInstructions?.trim() || data.stampingText?.trim() || data.stateNotes?.length || data.stateDocuments?.length);

const YesNo = ({ value }: { value: boolean | undefined }) =>
  value ? (
    <>
      <CheckIcon className="size-4" aria-hidden="true" />
      <span className="sr-only">Yes</span>
    </>
  ) : (
    <span className="text-muted-foreground">
      <span aria-hidden="true">—</span>
      <span className="sr-only">No</span>
    </span>
  );

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-base font-semibold">{title}</h2>
    {children}
  </section>
);

const StampWording = ({ data }: { data: TaxData }) => {
  const [showOfficial, setShowOfficial] = useState(false);
  const officialHtml = useMemo(() => sanitizeHtml(data.stampingText), [data.stampingText]);

  return (
    <Section title="State Stamp Wording">
      {data.stampingInstructions?.trim() ? (
        <p className="text-sm whitespace-pre-line">{data.stampingInstructions}</p>
      ) : (
        <p className="text-sm text-muted-foreground">No stamping instructions for this state.</p>
      )}
      {officialHtml && (
        <div className="flex flex-col items-start gap-2">
          <Button variant="link" size="sm" className="px-0" aria-expanded={showOfficial} onClick={() => setShowOfficial((shown) => !shown)}>
            {showOfficial ? <ChevronUpIcon data-icon="inline-start" /> : <ChevronDownIcon data-icon="inline-start" />}
            {showOfficial ? 'Hide' : 'Show'} official stamp wording
          </Button>
          {showOfficial && (
            <div className={cn('w-full rounded-lg border bg-muted/50 p-4 text-sm', RICH_TEXT)} dangerouslySetInnerHTML={{ __html: officialHtml }} />
          )}
        </div>
      )}
    </Section>
  );
};

const NoteItem = ({ note }: { note: StateNote }) => {
  const html = useMemo(() => sanitizeHtml(note.note), [note.note]);
  return (
    <li className="flex flex-col gap-1 rounded-lg border p-3 text-sm">
      {note.noteTypeLabel && <span className="text-xs font-medium text-muted-foreground">{note.noteTypeLabel}</span>}
      {html && <div className={cn(RICH_TEXT, note.bold && 'font-semibold', note.italic && 'italic')} dangerouslySetInnerHTML={{ __html: html }} />}
      {note.instructions?.trim() && <p className="whitespace-pre-line text-muted-foreground">{note.instructions}</p>}
    </li>
  );
};

const StateNotes = ({ notes }: { notes: StateNote[] }) => (
  <Section title="State Notes">
    {notes.length ? (
      <ul className="flex flex-col gap-2">
        {notes.map((note, index) => (
          <NoteItem key={`${note.noteType ?? ''}-${index}`} note={note} />
        ))}
      </ul>
    ) : (
      <p className="text-sm text-muted-foreground">No state notes.</p>
    )}
  </Section>
);

const DOCUMENT_COLUMNS: { header: string; className?: string; cell: (doc: StateDocument) => React.ReactNode }[] = [
  { header: 'Document', className: 'whitespace-normal min-w-40', cell: (doc) => doc.name || doc.code || '—' },
  { header: 'Group', className: 'whitespace-normal min-w-32', cell: (doc) => doc.document_group || '—' },
  {
    header: 'Txn Types',
    cell: (doc) =>
      doc.transaction_types
        ?.map((type) => type.short_code)
        .filter(Boolean)
        .join(', ') || '—',
  },
  { header: 'Include w/ Policy', cell: (doc) => <YesNo value={doc.include_with_policy} /> },
  { header: 'Diligent Effort', cell: (doc) => <YesNo value={doc.include_in_diligent_effort} /> },
  { header: 'Keep on Hand', cell: (doc) => <YesNo value={doc.keep_on_hand} /> },
  { header: 'Signature', className: 'whitespace-normal min-w-32', cell: (doc) => doc.signature_requirements || 'None' },
  {
    header: 'Download',
    cell: (doc) => {
      const href = safeUrl(doc.document_download_link);
      return href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
        >
          Download
          <ExternalLinkIcon className="size-3.5" aria-hidden="true" />
          <span className="sr-only">{doc.name} (opens in a new tab)</span>
        </a>
      ) : (
        '—'
      );
    },
  },
  { header: 'Notes', className: 'whitespace-pre-line min-w-64', cell: (doc) => doc.document_note || '' },
];

const StateDocuments = ({ documents }: { documents: StateDocument[] }) => (
  <Section title="State Documents">
    {documents.length ? (
      <Table>
        <TableHeader>
          <TableRow>
            {DOCUMENT_COLUMNS.map((column) => (
              <TableHead key={column.header} className="h-8">
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc, index) => (
            <TableRow key={doc.id ?? `${doc.code ?? ''}-${index}`}>
              {DOCUMENT_COLUMNS.map((column) => (
                <TableCell key={column.header} className={cn('py-1.5 align-top', column.className)}>
                  {column.cell(doc)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ) : (
      <p className="text-sm text-muted-foreground">No state documents.</p>
    )}
  </Section>
);

type BindInstructionsProps = {
  quoteId: string | null | undefined;
  /* The tab is mounted hidden with the rest of the quote page; nothing is fetched until it is first
     shown — the backend serves the stored copy, but calls InsCipher for a quote that has none yet. */
  active: boolean;
};

/* The quote's "Stamping & Bind Instructions" tab: InsCipher's stamp wording, state notes and state
   documents for the quote's home state. Fetched once per quote for as long as the page is open. */
export const BindInstructions = ({ quoteId, active }: BindInstructionsProps) => {
  const { getQuoteTaxes } = useApi();
  const [load, setLoad] = useState<Load | null>(null);

  const fetchTaxes = useCallback(
    (id: string) => {
      setLoad({ quoteId: id, status: 'loading' });
      Promise.resolve(getQuoteTaxes(id))
        .then((data: TaxData | null | undefined) =>
          setLoad((current) =>
            current?.quoteId === id ? { quoteId: id, status: 'loaded', data: data && typeof data === 'object' ? data : null } : current
          )
        )
        .catch(() => setLoad((current) => (current?.quoteId === id ? { quoteId: id, status: 'failed' } : current)));
    },
    [getQuoteTaxes]
  );

  useEffect(() => {
    if (active && quoteId && load?.quoteId !== quoteId) fetchTaxes(quoteId);
  }, [active, quoteId, load?.quoteId, fetchTaxes]);

  const current = load && load.quoteId === quoteId ? load : null;

  let body: React.ReactNode;
  if (!current || current.status === 'loading') {
    body = (
      <div className="flex w-full justify-center py-6">
        <Spinner />
      </div>
    );
  } else if (current.status === 'failed') {
    body = (
      <Alert variant="destructive">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          Couldn&apos;t load the stamping and bind instructions.
          <Button variant="outline" size="sm" onClick={() => quoteId && fetchTaxes(quoteId)}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (current.data?.calculationStatus === 'ERROR') {
    body = (
      <Alert variant="destructive">
        <AlertDescription>InsCipher couldn&apos;t calculate taxes for this quote.</AlertDescription>
      </Alert>
    );
  } else if (!hasContent(current.data)) {
    body = (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No stamping or bind instructions available for this quote.</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  } else {
    const { data } = current;
    body = (
      <>
        <StampWording data={data} />
        <Separator />
        <StateNotes notes={data.stateNotes ?? []} />
        <Separator />
        <StateDocuments documents={data.stateDocuments ?? []} />
      </>
    );
  }

  return <div className="flex flex-col gap-6 bg-background p-6">{body}</div>;
};
